/**
 * Footer editor.
 *
 * Columns and links are edited here and stored on the tenant in the database. The
 * platform defaults from config/platform.yaml arrive via the catalogue endpoint, so an
 * unedited tenant shows "inherited" and can be reset back to them — the override is
 * stored as absent rather than as a copy, which means a later change to the defaults
 * still reaches every tenant that never customised it.
 *
 * Editing is JSON-shaped rather than a drag-and-drop builder on purpose: the schema
 * validates it server side, and a text editor that round-trips exactly is more honest
 * than a widget that silently drops fields it does not understand.
 */

import { useEffect, useState } from 'react';
import { Banner, Button, Card } from '../../shared/ui';

export interface FooterLink {
  label: string;
  href: string;
}
export interface FooterConfig {
  columns?: { heading: string; links: FooterLink[] }[];
  legal?: FooterLink[];
  social?: { icon: string; href: string; label?: string }[];
}

export function FooterCard({
  saved,
  inherited,
  onSave,
  busy,
}: {
  saved: FooterConfig | null;
  inherited: FooterConfig;
  onSave: (patch: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const effective = saved ?? inherited;
  const [draft, setDraft] = useState<FooterConfig>(effective);
  const [error, setError] = useState<string | null>(null);

  // Re-sync when a save returns so the editor shows what the server stored, not what
  // was typed at it.
  useEffect(() => setDraft(saved ?? inherited), [saved, inherited]);

  const isOverridden = saved !== null;

  function updateColumn(index: number, next: { heading: string; links: FooterLink[] }) {
    const columns = [...(draft.columns ?? [])];
    columns[index] = next;
    setDraft({ ...draft, columns });
  }

  return (
    <Card
      title="Footer"
      action={
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {isOverridden && (
            <Button
              busy={busy}
              onClick={() => {
                // null clears the override; the resolver then falls back to defaults.
                onSave({ footer: null });
              }}
            >
              Reset to default
            </Button>
          )}
          <Button
            variant="primary"
            busy={busy}
            onClick={() => {
              const problem = validate(draft);
              setError(problem);
              if (!problem) onSave({ footer: draft });
            }}
          >
            Save footer
          </Button>
        </div>
      }
    >
      {!isOverridden && (
        <Banner tone="info">
          Showing the platform default. Saving creates an override for this academy only.
        </Banner>
      )}
      {error && <Banner tone="danger">{error}</Banner>}

      <div className="console__footer-cols">
        {(draft.columns ?? []).map((column, index) => (
          <div className="console__footer-col" key={index}>
            <input
              className="console__footer-heading"
              value={column.heading}
              aria-label={`Column ${index + 1} heading`}
              onChange={(e) => updateColumn(index, { ...column, heading: e.target.value })}
            />
            {column.links.map((link, linkIndex) => (
              <div className="console__footer-link" key={linkIndex}>
                <input
                  value={link.label}
                  aria-label="Link label"
                  placeholder="Label"
                  onChange={(e) => {
                    const links = [...column.links];
                    links[linkIndex] = { ...link, label: e.target.value };
                    updateColumn(index, { ...column, links });
                  }}
                />
                <input
                  className="console__footer-href"
                  value={link.href}
                  aria-label="Link URL"
                  placeholder="/path"
                  onChange={(e) => {
                    const links = [...column.links];
                    links[linkIndex] = { ...link, href: e.target.value };
                    updateColumn(index, { ...column, links });
                  }}
                />
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="ghost"
                  aria-label={`Remove ${link.label}`}
                  onClick={() =>
                    updateColumn(index, {
                      ...column,
                      links: column.links.filter((_, i) => i !== linkIndex),
                    })
                  }
                >
                  ×
                </button>
              </div>
            ))}
            <Button
              onClick={() =>
                updateColumn(index, {
                  ...column,
                  links: [...column.links, { label: 'New link', href: '#' }],
                })
              }
            >
              Add link
            </Button>
          </div>
        ))}
      </div>

      <p className="console__hint">
        Saved to the database and live on the next page load. Learners see these in the
        footer of every page on this academy.
      </p>
    </Card>
  );
}

/**
 * Mirror the cheapest schema rules so mistakes surface before the round trip. The
 * server validates authoritatively against config/schema/tenant.schema.json.
 */
function validate(footer: FooterConfig): string | null {
  const columns = footer.columns ?? [];
  if (columns.length > 4) return 'A footer can have at most four columns.';

  for (const column of columns) {
    if (!column.heading.trim()) return 'Every column needs a heading.';
    if (column.links.length > 8) return `"${column.heading}" has more than eight links.`;
    for (const link of column.links) {
      if (!link.label.trim()) return `A link in "${column.heading}" has no label.`;
      if (!link.href.trim()) return `"${link.label}" has no URL.`;
    }
  }
  return null;
}
